import { Modal } from "antd";
import { useCallback, useEffect, useRef } from "react";

/**
 * useUnsavedChangesGuard
 * แจ้งเตือนผู้ใช้เมื่อพยายามออกจากหน้าโดยยังมีข้อมูลที่ "ยังไม่บันทึก"
 *
 * ครอบคลุม 3 ช่องทาง:
 *  1) ปิด/รีเฟรชแท็บ (beforeunload) — แสดง prompt ของเบราว์เซอร์
 *  2) ปุ่ม Back/Forward ของเบราว์เซอร์ (popstate) — ดักด้วย sentinel history entry
 *  3) การนำทางภายในแอปผ่าน confirmLeave() — แสดง Modal.confirm ก่อนออก
 *
 * หมายเหตุ: โปรเจกต์ใช้ <BrowserRouter> แบบเก่า (react-router 6.4.1) ที่ไม่มี useBlocker
 * จึงดักการนำทางภายในแอปได้เฉพาะจุดที่เรียก confirmLeave() เท่านั้น
 * (ลิงก์เมนู/แถบข้างที่ไม่ผ่าน confirmLeave จะยังไม่ถูกดัก)
 *
 * @param isDirty มีข้อมูลที่ยังไม่บันทึกหรือไม่
 * @param onLeave callback ที่จะเรียกเมื่อผู้ใช้ยืนยัน "ออกโดยไม่บันทึก" จากปุ่ม Back ของเบราว์เซอร์
 */
export function useUnsavedChangesGuard(isDirty: boolean, onLeave?: () => void) {
  // เก็บ onLeave ล่าสุดไว้ใน ref เพื่อไม่ต้อง re-bind popstate listener ทุกครั้งที่ onLeave เปลี่ยน
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;

  // ป้องกันการเปิด Modal ยืนยันซ้อนกันหลายอัน (จาก popstate ที่ยิงถี่)
  const confirmingRef = useRef(false);

  // ---- 1) ปิด/รีเฟรชแท็บ ----
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // ---- 2) ปุ่ม Back/Forward ของเบราว์เซอร์ ----
  useEffect(() => {
    if (!isDirty) return;

    // วาง sentinel entry ไว้ 1 อัน เพื่อให้การกด Back ยิง popstate โดยยังไม่ออกจากหน้าจริง
    window.history.pushState(null, "", window.location.href);

    const onPopState = () => {
      if (confirmingRef.current) return;
      // re-trap ทันที เพื่อกันผู้ใช้ออกก่อนตัดสินใจ
      window.history.pushState(null, "", window.location.href);
      confirmingRef.current = true;
      Modal.confirm({
        title: "ข้อมูลยังไม่ถูกบันทึก",
        content: "คุณต้องการออกจากหน้านี้โดยไม่บันทึกการเปลี่ยนแปลงหรือไม่?",
        okText: "ออกโดยไม่บันทึก",
        okType: "danger",
        cancelText: "อยู่ต่อ",
        onOk: () => {
          confirmingRef.current = false;
          window.removeEventListener("popstate", onPopState);
          onLeaveRef.current?.();
        },
        onCancel: () => {
          confirmingRef.current = false;
        },
      });
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [isDirty]);

  // ---- 3) การนำทางภายในแอป (ปุ่มกลับ/ปุ่ม footer) ----
  const confirmLeave = useCallback(
    (leave: () => void) => {
      if (!isDirty) {
        leave();
        return;
      }
      Modal.confirm({
        title: "ข้อมูลยังไม่ถูกบันทึก",
        content: "คุณต้องการออกจากหน้านี้โดยไม่บันทึกการเปลี่ยนแปลงหรือไม่?",
        okText: "ออกโดยไม่บันทึก",
        okType: "danger",
        cancelText: "อยู่ต่อ",
        onOk: leave,
      });
    },
    [isDirty]
  );

  return { confirmLeave };
}
